import { Link } from "gatsby"
import { FaArrowRight } from "react-icons/fa6"
import { BlackArrowRight } from "../ds/Shapes"

export function Dropdown({
  data,
  isCaseStudy = false,
  singleColumn = false,
  align = "right",
  featureFirst = false,
  viewAllTo,
  viewAllLabel,
}) {
  const resolvedViewAllTo =
    viewAllTo || (isCaseStudy ? "/case-study/" : undefined)
  const resolvedViewAllLabel =
    viewAllLabel || (isCaseStudy ? "View all case studies" : undefined)

  if (singleColumn) {
    return (
      <SingleColumnDropdown
        data={data}
        align={align}
        featureFirst={featureFirst}
        viewAllTo={resolvedViewAllTo}
        viewAllLabel={resolvedViewAllLabel}
      />
    )
  }

  const numCols = data.length < 6 ? 2 : 3
  const itemsPerCol = Math.ceil(data.length / numCols)

  return (
    <div
      className={`w-fit mx-auto rounded-xl shadow-2xl border border-slate-100 transition opacity-0
      group-hover:opacity-100 invisible group-hover:visible absolute top-[60px]
      group-hover:translate-y-0 translate-y-2 mt-2 -left-40 -right-40 bg-white z-10
      overflow-hidden transition duration-300 ease-in-out`}
    >
      <div className="container mx-auto">
        <div className="p-8">
          <div className="flex -ml-4">
            {Array.from({ length: numCols }).map((_, colIndex) => (
              <div key={colIndex} className={`w-1/${numCols}`}>
                {data
                  .slice(
                    colIndex * itemsPerCol,
                    colIndex * itemsPerCol + itemsPerCol
                  )
                  .map((menu, index) => {
                    const itemClass =
                      "text-sm text-black rounded-xl p-4 block transition delay-10 duration-200 ease-in-out hover:bg-rose-50 mb-2"
                    const inner = (
                      <>
                        <h4 className="text-base text-black font-semibold mb-2">
                          {menu.title}
                        </h4>
                        <p className="text-xs text-zinc-500 font-normal">
                          {menu.description}
                        </p>
                      </>
                    )
                    const isExternal =
                      typeof menu.to === "string" && menu.to.startsWith("http")
                    return isExternal ? (
                      <a
                        key={index}
                        className={itemClass}
                        href={menu.to}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {inner}
                      </a>
                    ) : (
                      <Link key={index} className={itemClass} to={menu.to}>
                        {inner}
                      </Link>
                    )
                  })}
              </div>
            ))}
          </div>

          {isCaseStudy && (
            <Link
              to="/case-study/"
              className="flex items-center py-2 block mt-3"
            >
              <span className="mr-2 text-sm font-semibold text-black">
                View all
              </span>
              <BlackArrowRight className="transition delay-100 duration-300 ease-in-out" />
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}

function SingleColumnDropdown({
  data,
  align = "right",
  featureFirst = false,
  viewAllTo,
  viewAllLabel,
}) {
  const alignClass = align === "left" ? "left-0" : "right-0"

  return (
    <div
      className={`w-[280px] ${alignClass} absolute top-full pt-3 z-10
      transition opacity-0 group-hover:opacity-100 invisible group-hover:visible
      group-hover:translate-y-0 translate-y-2 duration-300 ease-in-out`}
    >
      <div className="rounded-xl shadow-2xl border border-slate-100 bg-white overflow-hidden p-2">
        {featureFirst ? (
          <>
            {data[0] && <DropdownItem item={data[0]} featured />}
            {data.length > 1 && (
              <>
                <div className="h-px bg-slate-100 my-2 mx-3" />
                {data.slice(1).map((item, index) => (
                  <DropdownItem key={index} item={item} />
                ))}
              </>
            )}
          </>
        ) : (
          data.map((item, index) => <DropdownItem key={index} item={item} />)
        )}
        {viewAllTo && viewAllLabel && (
          <>
            <div className="h-px bg-slate-100 my-2 mx-3" />
            <Link
              to={viewAllTo}
              className="group/item flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold text-black transition duration-150 ease-in-out hover:bg-rose-50"
            >
              <span>{viewAllLabel}</span>
              <span className="transition duration-150 ease-in-out group-hover/item:translate-x-1">
                <FaArrowRight size={14} />
              </span>
            </Link>
          </>
        )}
      </div>
    </div>
  )
}

function DropdownItem({ item, featured = false }) {
  const className = `group/item flex items-center justify-between rounded-lg px-3 py-2.5 transition duration-150 ease-in-out hover:bg-rose-50 ${
    featured ? "text-black font-semibold" : "text-zinc-700 font-medium"
  } text-sm`
  const inner = (
    <>
      <span>{item.title}</span>
      <span className="opacity-0 -translate-x-1 transition duration-150 ease-in-out group-hover/item:opacity-100 group-hover/item:translate-x-0">
        <FaArrowRight size={14} />
      </span>
    </>
  )
  const isExternal =
    typeof item.to === "string" && item.to.startsWith("http")
  return isExternal ? (
    <a
      className={className}
      href={item.to}
      target="_blank"
      rel="noopener noreferrer"
    >
      {inner}
    </a>
  ) : (
    <Link className={className} to={item.to}>
      {inner}
    </Link>
  )
}
